"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { PhoneCall, CircleQuestionMark as HelpCircle, ShieldCheck, Clock, Wrench } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const faqList = [{
  id: "item-1",
  question: "What are the earliest warning signs of a hidden pipe leak?",
  answer: "Common warning signs include unexplained spikes in your water bill, damp or discolored spots on drywall and ceilings, musty odors, warm spots on flooring (often slab leaks), and a sound of running water even when all fixtures are turned off."
}, {
  id: "item-2",
  question: "How quickly can your plumbers respond to a burst pipe in Jonesboro?",
  answer: "We offer 24/7 emergency dispatch across Jonesboro and surrounding Clayton County areas. In urgent burst pipe situations, our licensed technicians typically arrive within 45 to 60 minutes equipped with heavy-duty repair tools and replacement piping."
}, {
  id: "item-3",
  question: "Should I repair or completely replace an aging, leaking pipe?",
  answer: "Isolated leaks caused by loose fittings or small pinholes in otherwise solid pipes can usually be repaired with sectional fixes. However, if your home still has older galvanized steel or polybutylene pipes with widespread corrosion, full repiping is safer and saves money in the long run."
}, {
  id: "item-4",
  question: "What should I do immediately while waiting for emergency pipe repair?",
  answer: "First, locate and turn off your main water shutoff valve immediately to prevent further structural damage. Open cold-water faucets at the lowest level of the house to drain remaining pressure, turn off power to water heaters if applicable, and move valuables away from wet zones."
}, {
  id: "item-5",
  question: "How much does emergency pipe repair typically cost in Jonesboro, GA?",
  answer: "Costs depend on accessibility, severity, and pipe material. Minor exposed pipe repairs range from $150 to $350, while concealed ceiling, wall, or slab repairs may vary. We always provide upfront, flat-rate pricing before starting any work so there are no surprises."
}];
export default function PipeFaq() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="piperepairfaq" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          
          {/* Left Column: Heading, intro & emergency card */}
          <div className="lg:col-span-5 space-y-6">
            <div className="space-y-3">
              <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold gap-1.5 rounded-full">
                <HelpCircle className="size-3.5 text-primary" />
                <span>Pipe Repair Questions</span>
              </Badge>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground font-default">
                Clear Answers on Pipe Repairs & Emergency Leaks
              </h2>
              <p className="text-muted-foreground text-base leading-relaxed">
                Got a leaking, cracked, or noisy pipe in Jonesboro? Here are clear, straightforward answers to the questions homeowners ask us most frequently.
              </p>
            </div>

            {/* Support info card */}
            <Card className="border border-border bg-card shadow-sm rounded-xl">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="size-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <Wrench className="size-5" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-card-foreground text-base font-default">
                      Still Have Questions?
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      Our Jonesboro plumbers are ready 24 hours a day.
                    </p>
                  </div>
                </div>

                <div className="pt-2 border-t border-border flex flex-col sm:flex-row gap-3">
                  <Button asChild className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-medium">
                    <a href="tel:4708479076" className="flex items-center justify-center gap-2">
                      <PhoneCall className="size-4" />
                      <span>Call Now: (470) 847-9076</span>
                    </a>
                  </Button>
                  <Button asChild variant="outline" className="w-full">
                    <Link to="/contact/">Contact Team</Link>
                  </Button>
                </div>

                <div className="grid grid-cols-2 gap-3 pt-2 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1.5">
                    <Clock className="size-4 text-primary shrink-0" />
                    <span>24/7 Rapid Dispatch</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <ShieldCheck className="size-4 text-primary shrink-0" />
                    <span>Licensed & Insured</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column: Accordion Items */}
          <div className="lg:col-span-7">
            <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
            once: true,
            amount: 0.2
          }} className="space-y-4">
              <Accordion type="single" collapsible defaultValue="item-1" className="w-full space-y-4">
                {faqList.map((faq, index) => <motion.div key={faq.id} variants={itemVariants} data-index={index}>
                    <AccordionItem value={faq.id} className="border border-border bg-card rounded-xl px-5 py-1 shadow-xs transition-colors duration-200 hover:border-primary/40 data-[state=open]:border-primary/50 data-[state=open]:shadow-sm">
                      <AccordionTrigger className="text-left text-base sm:text-lg font-semibold text-foreground py-4 hover:no-underline [&[data-state=open]]:text-primary font-default">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-muted-foreground text-sm sm:text-base leading-relaxed pb-4 pt-1">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  </motion.div>)}
              </Accordion>
            </motion.div>
          </div>

        </div>
      </div>
    </section>;
}